import React, { createContext, useContext, useState, useEffect } from 'react';
import auditLogger from '../utils/auditLogger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for stored user data on component mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      console.log('Attempting login for:', username);

      const response = await fetch('https://chappie-demo.novosense.africa:6003/api/wdsf/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      console.log('Login response status:', response.status);

      if (response.ok) {
        const userData = await response.json();
        console.log('Login successful:', userData);
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));

        // Log successful login
        auditLogger.logLogin(userData.username || userData.id);

        return { success: true, user: userData };
      } else {
        const errorData = await response.json();
        console.log('Login failed:', errorData);
        return { success: false, error: errorData.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    // Log logout before clearing user data
    const currentUser = user;
    setUser(null);
    localStorage.removeItem('user');

    if (currentUser) {
      auditLogger.logLogout(currentUser.username || currentUser.id);
    }
  };

  // Role-based permission helpers
  const hasPermission = (permission) => {
    if (!user || !user.type) return false;

    const userRole = user.type.toLowerCase();

    switch (permission) {
      case 'administration':
        return userRole === 'master' || userRole === 'admin';
      case 'screen-management':
        return userRole === 'master';
      case 'scrutineer':
        return userRole === 'master' || userRole === 'scrutineer';
      case 'compere':
        return userRole === 'master';
      case 'chair-adjudicator':
        return userRole === 'master' || userRole === 'chairperson';
      case 'running-list':
        return userRole === 'master' || userRole === 'admin';
      case 'marshall-area':
        return userRole === 'master';
      case 'audit-trail':
        return userRole === 'master';
      case 'competition-management':
        return userRole === 'master' || userRole === 'admin';
      default:
        return userRole === 'master';
    }
  };

  const getAccessibleRoutes = () => {
    if (!user || !user.type) return [];

    const userRole = user.type.toLowerCase();
    const routes = [];

    switch (userRole) {
      case 'master':
        routes.push(
          '/administration',
          '/screen-management',
          '/scrutineer',
          '/compere',
          '/audit-trail'
        );
        break;
      case 'admin':
        routes.push(
          '/administration'
          // Running List is handled as external popup
        );
        break;
      case 'scrutineer':
        routes.push('/scrutineer');
        break;
      case 'chairperson':
        // Chair Adjudicator is handled as external popup
        break;
      default:
        break;
    }

    return routes;
  };

  const getAccessibleExternalPages = () => {
    if (!user || !user.type) return [];

    const userRole = user.type.toLowerCase();
    const pages = [];

    switch (userRole) {
      case 'master':
        pages.push(
          { name: 'Running List', key: 'running-list' },
          { name: 'Marshall Area', key: 'marshall-area' },
          { name: 'Chair Adjudicator', key: 'chair-adjudicator' }
        );
        break;
      case 'admin':
        pages.push(
          { name: 'Running List', key: 'running-list' }
        );
        break;
      case 'scrutineer':
        break;
      case 'chairperson':
        pages.push(
          { name: 'Chair Adjudicator', key: 'chair-adjudicator' }
        );
        break;
      default:
        break;
    }

    return pages;
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    hasPermission,
    getAccessibleRoutes,
    getAccessibleExternalPages,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};